import { canMemberSendMessageInTaskChat } from '@frontend/api-client';
import type { LocalFile } from '@frontend/api-types';
import { useCreateTaskCommentForm } from '@frontend/forms';
import { YStack } from '@frontend/web-react';
import { ChatFooter } from '@frontend/web/components';
import { LowBandwidthChatBanner } from '@frontend/web/components/DomainSpecific/Tasks/Chat/LowBandwidthChatBanner';
import { handleMutation } from '@frontend/web/utils';
import { memo } from 'react';

export type SubmitComment = (body: string, attachments?: LocalFile[]) => Promise<void>;

export type TaskChatFooterContentProps = {
  hasPendingGameplan: boolean;
  isClientSubscribed: boolean;
  isGameplanGenerating: boolean;
  onCommentCreated: () => void;
  submitComment: SubmitComment;
  task: Parameters<typeof canMemberSendMessageInTaskChat>[0] | undefined;
};

export const TaskChatFooterContent = memo(
  ({
    hasPendingGameplan,
    isClientSubscribed,
    isGameplanGenerating,
    onCommentCreated,
    submitComment,
    task,
  }: TaskChatFooterContentProps) => {
    const { handleSubmit, isSubmitting, setFieldValue, values } = useCreateTaskCommentForm({
      onSubmit: async (values, { resetForm }) =>
        handleMutation({
          mutation: () => submitComment(values.body || 'See attached', values.attachments),
          onSuccess: () => {
            resetForm();
            onCommentCreated();
          },
          showErrorToast: true,
          showSuccessToast: false,
        }),
    });

    const shouldBlockChat = isGameplanGenerating || hasPendingGameplan;

    if (!task || !canMemberSendMessageInTaskChat(task) || !isClientSubscribed) {
      return null;
    }

    return (
      <YStack className="w-full max-w-screen-md self-center gap-sm">
        <LowBandwidthChatBanner />
        <ChatFooter
          className="pb-md"
          filesValue={values.attachments}
          isSubmitLoading={isSubmitting}
          muted={shouldBlockChat}
          onChangeFiles={(attachments) => setFieldValue('attachments', attachments)}
          onChangeText={(body) => setFieldValue('body', body)}
          onClickSubmit={handleSubmit}
          placeholder={
            isGameplanGenerating
              ? 'Generating gameplan...'
              : hasPendingGameplan
                ? 'Accept or decline gameplan to continue'
                : 'Enter a message...'
          }
          submitDisabled={shouldBlockChat || (!values.body && !values.attachments.length)}
          submitShortcut="Enter"
          textInputDisabled={shouldBlockChat || isSubmitting}
          textValue={values.body}
        />
      </YStack>
    );
  },
);
